import { isEmptyOrWhitespace, observable, ViewModelBase } from "@shoothill/core";
import type { ValidationResponse } from "@shoothill/core";
import { action, computed } from "mobx";

import { formatCurrencyFromPounds, formatDate } from "Utils/Format";
import { RequisitionRequestItemModel } from "./RequisitionRequestItemModel";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { AppUrls } from "AppUrls";

export class RequisitionRequestItemViewModel extends ViewModelBase<RequisitionRequestItemModel> {
    // #region Constructors and Disposers

    constructor(requisitionRequestItemModel: RequisitionRequestItemModel) {
        super(requisitionRequestItemModel);
        this.setDecorators(RequisitionRequestItemModel);
    }

    // #region Constructors and Disposers

    // #region Properties

    @observable
    public isHire: boolean = false;

    @action
    public setIsHire = (val: boolean) => {
        this.isHire = val;
    };

    @observable
    public isHireLegacy: boolean = false;

    @action
    public setIsHireLegacy = (val: boolean) => {
        this.isHireLegacy = val;
    };

    @computed
    public get hasId(): boolean {
        return this.model.id !== "" && this.model.id !== undefined && this.model.id !== null;
    }

    @computed
    public get itemDescription(): string {
        return this.model.itemDescription;
    }

    @computed
    public get units(): number {
        return this.model.units;
    }

    @computed
    public get unitPrice(): string {
        return formatCurrencyFromPounds(this.model.unitPrice, 2, 4);
    }

    @computed
    public get total(): string {
        return formatCurrencyFromPounds(this.model.total);
    }

    @computed
    public get isStockOrderLine() {
        return !isEmptyOrWhitespace(this.model.materialId);
    }

    @computed
    public get dateFromFormatted(): string {
        return this.model.dateFrom ? formatDate(this.model.dateFrom) : "";
    }

    @computed
    public get dateToFormatted(): string {
        return this.model.dateTo ? formatDate(this.model.dateTo) : "";
    }

    @computed
    public get isNAFormatted(): string {
        return this.model.isNA ? "Hire period: N/A" : "Hire period:";
    }

    @computed
    public get isNAWithDatesFormatted(): string {
        return this.model.isNA ? this.isNAFormatted : `${this.isNAFormatted} ${this.dateFromFormatted} - ${this.dateToFormatted}`;
    }

    // #endregion Properties

    // #region Actions

    @action
    public voidItemUnits = () => {
        this.model.units = 0;
    };

    @computed
    public get canVoidItemUnits() {
        return !isEmptyOrWhitespace(this.model.id) && this.model.units > 0 && this.isStockOrderLine;
    }

    @action
    public setDeleted = () => {
        this.model.isDeleted = true;
    };

    @computed
    public get canDelete(): boolean {
        return this.model.id === "" || this.model.id === null || this.model.id === undefined || this.model.canDeleteItem;
    }

    // #endregion Actions

    // #region Api Actions

    public server: ServerViewModel = new ServerViewModel();

    public apiVoidItemUnits = async () => {
        await this.server.query<any>(
            () => this.Get(AppUrls.Server.PurchaseOrder.CanVoidOrderLine.replace("{orderLineId}", this.model.id!)),
            (result) => {
                if (result) {
                    this.voidItemUnits();
                } else {
                    this.setSnackMessage("This order line cannot be voided as it has associated stock in use.");
                    this.setSnackType(this.SNACKERROR);
                    this.setSnackbarState(true);
                }
            },
        );

        if (this.server.HaveValidationMessage) {
            this.setSnackMessage(this.server.ValidationMessage);
            this.setSnackType(this.SNACKERROR);
            this.setSnackbarState(true);
        }
    };

    // #endregion Api Actions

    @computed
    private get validateItemDescription(): ValidationResponse {
        const errorMessage = this.model.validateItemDescription;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateUnits(): ValidationResponse {
        const errorMessage = this.model.validateUnits;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateUnitPrice(): ValidationResponse {
        const errorMessage = this.model.validateUnitPrice;

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    // #region Dates for Hire Order Type

    @computed
    private get validateFromDate(): ValidationResponse {
        // RULES
        // The date must be defined if the order type is hire.
        let errorMessage = "";

        if (this.isHireLegacy) {
            errorMessage = ""; // Handled on the PO.
        } else {
            errorMessage = this.isHire && !this.model.isNA && this.model.dateFrom === RequisitionRequestItemModel.DEFAULT_DATEFROM ? "Please provide a date" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    @computed
    private get validateToDate(): ValidationResponse {
        // RULES
        // The date must be defined if the order type is hire.
        let errorMessage = "";

        if (this.isHireLegacy) {
            errorMessage = ""; // Handled on the PO.
        } else {
            errorMessage = this.isHire && !this.model.isNA && this.model.dateTo === RequisitionRequestItemModel.DEFAULT_DATETO ? "Please provide a date" : "";
        }

        return {
            errorMessage: errorMessage,
            isValid: isEmptyOrWhitespace(errorMessage),
        };
    }

    // #endregion Dates for Hire Order Type

    // #region Snack Bar

    public SNACKERROR = "error";

    @observable
    public snackbarState = false;

    @observable
    public snackType = "";

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @observable
    public snackMessage = "";

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    // #endregion Snack Bar

    // #region Boilerplate

    public async isFieldValid(fieldName: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        switch (fieldName) {
            case "itemDescription": {
                const result = this.validateItemDescription;

                errorMessage = result.errorMessage;
                isValid = result.isValid;
                break;
            }

            case "units": {
                const result = this.validateUnits;

                errorMessage = result.errorMessage;
                isValid = result.isValid;
                break;
            }

            case "unitPrice": {
                const result = this.validateUnitPrice;

                errorMessage = result.errorMessage;
                isValid = result.isValid;
                break;
            }

            case "dateFrom": {
                const result = this.validateFromDate;

                errorMessage = result.errorMessage;
                isValid = result.isValid;
                break;
            }

            case "dateTo": {
                const result = this.validateToDate;

                errorMessage = result.errorMessage;
                isValid = result.isValid;
                break;
            }
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Boilerplate
}
